import { Component, OnInit, OnDestroy, Input, ViewChild, signal } from '@angular/core';
import { LoadCaseService } from '../../services/load-case.service';
import { forkJoin, lastValueFrom, Observable } from 'rxjs';
import { ConfirmationService, SelectItem } from 'primeng/api';
import { PeriforOnChangeMessages, SignalRService } from '../../../shared/services/signal-r.service';
import { UdtService } from '../../../shared/services/udt.service';
import { WellConfigService } from '../../../shared/services/well-config.service';
import { TrajectoryService } from 'src/app/shared/services/trajectory.service';
import { LoadCaseDefinitionModel, LoadCaseSummaryResult } from '../../models/load-case.model';
import { catchError, map } from 'rxjs/operators';
import { PostInstallLoad, PostInstallLoadUi } from './post-install-load-details/models/post-install-load.model';
import { TubularString } from 'src/app/wellbore-inputs/models/well-schematic.model';
import { CementingLandingService } from 'src/app/shared/services/cementing-landing.service';
import { PoreAndFracService } from '../../services/pore-and-frac.service';
import { CementingLanding } from 'src/app/wellbore-inputs/models/cementing-landing.model';
import { PoreFrac } from '../../models/poreFrac.model';
import { BaseOperation } from 'src/app/perical/models/thermal-operation.model';
import { WellTypeService } from 'src/app/wellbore-inputs/services/well-type-datums.service';
import { WellType } from 'src/app/shared/models/wellType.model';
import { UndisturbedTemperature, UndisturbedTemperaturePlot } from 'src/app/wellbore-inputs/models/undisturbed-temperature.model';
import { UserUnitsModel } from 'src/app/core/components/user-units/user-units.model';
import { UserRoles } from 'src/app/core/components/user-admin-page/user-model';
import { DataSavedMessage } from 'src/app/shared/models/mediator-messages.model';
import { MediatorService } from 'src/app/shared/services/mediator.service';
import { SessionStorageKeys, StorageKeys, StoreService } from 'src/app/core/services/store.service';
import { Store } from '@ngneat/elf';
import { withUIEntities } from '@ngneat/elf-entities';
import { XyLinePlotUi } from 'src/app/core/models/xy-line-plot.model';
import { WellComponent } from 'src/app/wellbore-inputs/components/well-configuration/models';
import { CurrentAppIdsDto } from 'src/app/core/models/current-app-ids-dto';

@Component({
  selector: 'app-post-install-loads',
  templateUrl: './post-install-loads.component.html',
  providers: [ConfirmationService],
  standalone: false
})
export class PostInstallLoadsComponent implements OnInit, OnDestroy {
  public selectedString: string;
  public plotName: string;
  public userUnits: UserUnitsModel;
  public plotData: any;
  public loadDetailsDialog: boolean;
  public loadCases: LoadCaseSummaryResult[];
  public selectedLoad: LoadCaseSummaryResult;
  public lastSelectedLoad: LoadCaseSummaryResult;
  public loadCase: PostInstallLoad;
  public wellType: WellType;
  public selectedLoadCaseIndexes: number[] = [];
  public tubulars: WellComponent[];

  public pressTemp: SelectItem[] = [
    { label: 'Press', value: 'press' },
    { label: 'Temp', value: 'temp' }
  ];

  public tablePlot: SelectItem[] = [
    { label: 'Plot', value: 'plot' },
    { label: 'Grid', value: 'grid' }
  ];

  public configMissing: boolean;
  public currentString: TubularString;
  public shoeDepthTvd: number;
  public hangerTvd: number;
  public porePressures: PoreFrac[];
  public fracPressures: PoreFrac[];
  public cement: CementingLanding;
  public thermalOperations: Partial<BaseOperation>[];
  public udtData: UndisturbedTemperature;
  public udtPlotData: UndisturbedTemperaturePlot;
  public isLoading = signal<boolean>(true);
  public isLoadingPlot = signal<boolean>(false);
  private deletedLoadIndex: number;
  private isSingleClick: boolean;
  public isDelete: boolean;
  public expandedRows: { [key: string]: boolean } = {};
  public newLoadCaseDialogDisplay: boolean;
  public stringName: string;
  public wellTd: number;
  public userRoles: UserRoles;

  @ViewChild('loadsTable') loadsTable;

  //State Management
  private _componentId: string;
  @Input() set componentId(value: string) {
    this._componentId = value;
    this.postInstallLoadsStore = this._storeService.createStore(this.componentId, new PostInstallLoadUi, withUIEntities<XyLinePlotUi>());
    this.drawerOpen = this.postInstallLoadsStore.state.drawerOpen ?? false;
    this.pressTempSelected = this.postInstallLoadsStore.state.pressTempSelected || 'press';
    this.tablePlotSelected = this.postInstallLoadsStore.state.tablePlotSelected || 'plot';
    this.selectedLoad = this.postInstallLoadsStore.state.selectedLoad;
  }
  get componentId(): string {
    return this._componentId;
  }
  public postInstallLoadsStore: Store;

  public drawerOpen: boolean = false;
  public pressTempSelected: string = 'press';
  public tablePlotSelected: string = 'plot';

  // Track whether all are selected
  public selectAllChecked = false;

  constructor(
    private _loadCaseService: LoadCaseService,
    private _confirmationService: ConfirmationService,
    private _signalRService: SignalRService,
    private _udtService: UdtService,
    private _wellConfigService: WellConfigService,
    private _trajectoryService: TrajectoryService,
    private _cementingLandingService: CementingLandingService,
    private _poreAndFracService: PoreAndFracService,
    private _wellTypeService: WellTypeService,
    private _messenger: MediatorService,
    private _storeService: StoreService
  ) {
    this.isLoading.set(true);
  }

  async ngOnInit(): Promise<void> {
    this.userRoles = await this._storeService.get<UserRoles>(StorageKeys.ROLES);
    this.userUnits = await this._storeService.get<UserUnitsModel>(StorageKeys.UNITS);

    const appIds = this._storeService.getSession<CurrentAppIdsDto>(SessionStorageKeys.APP_IDS);
    this.selectedString = appIds.tubularId;
    this.getConfigAndUdt('stringChange');

    const hub = this._signalRService.getConnectionToNotificationHub();
    this._signalRService.subscribeToEventFilteredByDesignId(hub, SignalRService.ON_PFB_CHANGE, d => this.signalRfunc(d));
  }

  signalRfunc(data: any) {
    if (data.action === PeriforOnChangeMessages.REFRESH_TUBULARS_LIST || data.action === PeriforOnChangeMessages.REFRESH_PACKERS) {
      this.getConfigAndUdt('onInit');
      this.selectedLoad = this.loadCases[this.loadCases.length - 1];
      this.postInstallLoadsStore.update(state => ({ ...state, selectedLoad: this.selectedLoad }));
    }
    
    if (data.action == PeriforOnChangeMessages.REFRESH_STRING_INPUTS) {
      this.selectedLoadCaseIndexes = [];
      this.selectAllChecked = false;
      this.configMissing = false;
      const appIds = this._storeService.getSession<CurrentAppIdsDto>(SessionStorageKeys.APP_IDS);
      this.selectedString = appIds.tubularId;
      this.getConfigAndUdt('stringChange');
      this.refreshLoadCases('stringChange');
      this.expandedRows = {};
      this.selectedLoad = this.loadCases[0];
      this.postInstallLoadsStore.update(state => ({ ...state, selectedLoad: this.selectedLoad }));
      this.setPlotName();
    }

    if (data.action == PeriforOnChangeMessages.REFRESH_UDT_PLOT) {
      this.getUdtPlotData();
    }
  }

  private getConfigAndUdt(action): void {
    this.isLoading.set(true);
    this._wellConfigService.getTubular().subscribe({
      next: (currentString) => {
        if (currentString !== null) {
          this.currentString = currentString;
          const sources: Observable<any>[] = [
            this._cementingLandingService.getCementingLanding(null) as Observable<CementingLanding>,
            this._poreAndFracService.getPorePressures() as Observable<PoreFrac[]>,
            this._loadCaseService.getThermalOperations() as Observable<Partial<BaseOperation>[]>,
            this._wellTypeService.getWellType() as Observable<WellType>,
            this._trajectoryService.getTotalDepth() as Observable<any>,
            this._wellConfigService.getTubulars() as Observable<WellComponent[]>,
            this._poreAndFracService.getFractureGradient() as Observable<PoreFrac[]>
          ];

          forkJoin(sources).pipe(
            map(async ([cement, porePressures, thermalOperations, wellType, totalDepth, tubulars, fracGradient]) => {
              this.porePressures = porePressures;
              this.fracPressures = fracGradient;
              this.cement = cement;
              this.thermalOperations = thermalOperations;
              this.wellType = wellType;
              this.stringName = currentString.type;
              this.wellTd = totalDepth.measuredDepth;
              this.tubulars = tubulars;

              const tvds = await lastValueFrom(this._trajectoryService.getTvdsFromMds([currentString.hangerMd, currentString.shoeMd]));

              const hanger = tvds[0];
              const shoe = tvds[1];

              this.shoeDepthTvd = +shoe.toFixed(2);
              this.hangerTvd = +hanger.toFixed(2);

              this.configMissing = false;
              this.refreshLoadCases(action);
            }),
            catchError(err => {
              this.configMissing = true;
              this.isLoading.set(false);
              return err;
            })).subscribe();
        } else {
          this.configMissing = true;
          this.isLoading.set(false);
        }
      },
      error: () => {
        this.configMissing = true;
        this.isLoading.set(false);
      }
    });
  }

  setPlotName(editLoad?: string): string {
    return 'postInstallLoadsPlot - ' + this.selectedString + ' - ' + this.selectedLoad?.name + ' - ' + this.pressTempSelected + ' - ' + editLoad;
  }

  private refreshLoadCases(action, loadCase?): void {
    this.isLoading.set(true);
    this._loadCaseService.getLoadCasesSummary(true).subscribe({
      next: (loads) => {
        this.loadCases = [];
        if (this.currentString.type == 'Tubing' || !this.currentString?.hasCement || this.currentString.topOfCementMd >= this.currentString.shoeMd) {
          loads.forEach(x => {
            if (x.eppDisplayName == 'Fluid to Shoe') {
              x.eppDisplayName = 'Annular Fluid';
            }

            if (x.ippDisplayName == 'Displacement Fluid' && !this.currentString?.hasCement) {
              x.ippDisplayName = 'Completion Fluid';
            }

            if (x.eppDisplayName == 'Mud + Wet Cement' && (!this.currentString?.hasCement || this.currentString.topOfCementMd >= this.currentString.shoeMd)) {
              x.eppDisplayName = 'Mud/Brine to Shoe';
            }
          });
        }
        this.loadCases = loads;

        if (action === 'clone') {
          this.selectedLoad = loads.find(x => x.name === loadCase.name);
          this.postInstallLoadsStore.update(state => ({ ...state, selectedLoad: this.selectedLoad }));
          this.editLoadCase(this.selectedLoad);
        }

        if (!this.selectedLoad && action === 'stringChange') {
          this.selectedLoad = loads[loads.length - 1];
          this.postInstallLoadsStore.update(state => ({ ...state, selectedLoad: this.selectedLoad }));
        }

        if (action === 'newLoad') {
          this.selectedLoad = loads[loads.length - 1];
          this.postInstallLoadsStore.update(state => ({ ...state, selectedLoad: this.selectedLoad }));
          this.editLoadCase(this.selectedLoad);
          this.isLoading.set(false);
        }

        if (action === 'refresh' && !this.selectedLoad && this.loadCases.find(l => l.index === this.selectedLoad?.index)) {
          this.selectedLoad = loads.find(x => x.index === this.lastSelectedLoad.index);
          this.postInstallLoadsStore.update(state => ({ ...state, selectedLoad: this.selectedLoad }));
        }

        if (action === 'delete') {
          if (this.selectedLoad?.index === this.deletedLoadIndex) {
            this.selectedLoad = this.loadCases[this.loadCases.length - 1];
            this.postInstallLoadsStore.update(state => ({ ...state, selectedLoad: this.selectedLoad }));
            if (this.selectedLoad?.index === this.deletedLoadIndex) {
              this.selectedLoad = this.loadCases[this.loadCases.length - 2];
              this.postInstallLoadsStore.update(state => ({ ...state, selectedLoad: this.selectedLoad }));
            }
          }
        }

        if (action === 'deleteMultiple') {
          this.selectedLoad = this.loadCases[this.loadCases.length - 1];
          this.postInstallLoadsStore.update(state => ({ ...state, selectedLoad: this.selectedLoad }));
        }

        // Refresh Plots
        if (!this.selectedLoad && this.loadCases.length > 0) {
          this.selectedLoad = this.loadCases[this.loadCases.length - 1];
          this.postInstallLoadsStore.update(state => ({ ...state, selectedLoad: this.selectedLoad }));
        }

        if (!this.drawerOpen) {
          this.isLoading.set(false);
          return;
        }

        if (loads.length < 1) {
          this.selectedLoad = null;
          this.postInstallLoadsStore.update(state => ({ ...state, selectedLoad: null }));
          this.plotData = null;
          this.isLoading.set(false);
          return;
        }
        this.pressTempToggle();
        this.isLoading.set(false);
      },
      error: () => {
        this.isLoading.set(false);
      }
    });
  }

  private getUdtPlotData() {
    this._udtService.getUndisturbedTemperaturePlot().subscribe((res) => {
      this.udtPlotData = res;
    });
  }

  // Plot Actions
  drawerToggle() {
    this.drawerOpen = !this.drawerOpen;
    this.postInstallLoadsStore.update(state => ({ ...state, drawerOpen: this.drawerOpen }));
    if (this.drawerOpen) {
      this.isLoading.set(true);
      this.plotData = null;
      this.getUdtPlotData();
      this.isLoading.set(false);
    }
  }

  pressTempToggle(e?) {
    this.isLoadingPlot.set(true);
    if (!this.selectedLoad || !this.drawerOpen) {
      this.plotData = null;
      this.isLoadingPlot.set(false);
      return;
    }

    if (e) {
      this.pressTempSelected = e.value;
      this.postInstallLoadsStore.update(state => ({
        ...state,
        pressTempSelected: e.value
      }));
    } else if (this.loadCases.findIndex(x => x.index === this.selectedLoad?.index) === -1) {
      this.selectedLoad = this.loadCases[0];
      this.postInstallLoadsStore.update(state => ({
        ...state,
        pressTempSelected: 'press',
        selectedLoad: this.selectedLoad
      }));
    }

    this.plotName = '';
    this._loadCaseService.getPressuresAndTemperatures(this.selectedLoad.index).subscribe((data) => {
      if (this.pressTempSelected === 'temp') {
        this.getUdtPlotData();
      }
      this.plotData = data;
      this.plotName = this.setPlotName();
      this.isLoadingPlot.set(false);
    });
  }

  tablePlotToggle(e): void {
    this.tablePlotSelected = e.value;
    this.postInstallLoadsStore.update(state => ({
      ...state,
      tablePlotSelected: e.value
    }));
  }

  // Table Actions
  showNewLoadCaseDialog(): void {
    this.newLoadCaseDialogDisplay = true;
  }

  cancelNewLoadCaseDialog(): void {
    this.newLoadCaseDialogDisplay = false;
  }

  newLoadCase(name: string): void {
    this.isLoading.set(true);
    this._loadCaseService.addLoadCaseDefinition({ name: name.trim() }).subscribe(() => {
      this.refreshLoadCases('newLoad');
      this.newLoadCaseDialogDisplay = false;
    });
    // this.newLoadRef = this._dialogService.open(NewPostInstallLoadDialogComponent, {
    //   header: 'New Load Case',
    //   // width: '575px',
    //   // height: '268px',
    //   // contentStyle: { overflow: 'auto' },
    //   // keepInViewport: true
    //   // baseZIndex: 10000
    // });

    // this._subscriptions.add(this.newLoadRef.onClose.subscribe((dataChanged: boolean) => {
    //   if (dataChanged) {
    //     this.refreshLoadCases('newLoad');
    //   }
    // }));
  }

  editLoadCase(load: LoadCaseSummaryResult): void {
    this.isSingleClick = false;
    this.isLoading.set(true);
    this.selectedLoad = load;
    this.postInstallLoadsStore.update(state => ({ ...state, selectedLoad: this.selectedLoad }));
    this.expandedRows = {};
    this.expandedRows[this.selectedLoad.index] = true;
    this.lastSelectedLoad = load;
    this.loadCase = null;
    this._loadCaseService.getLoadCase(load.index, null).subscribe({
      next: (loadCase) => {
        this.loadCase = loadCase;
        this.isLoading.set(false);

        const index = this.loadCases.findIndex(x => x.index === load.index);
        const loadTop = this.loadsTable.wrapperViewChild.nativeElement.querySelectorAll('tr')[index].offsetTop - 9;

        setTimeout(() => {
          this.loadsTable?.scrollTo({
            top: loadTop,
            behavior: 'smooth'
          });
        }, 100);
      },
      error: () => {
        this.isLoading.set(false);
      }
    });

    if (!this.drawerOpen) {
      return;
    }
    this.pressTempToggle();
  }

  cancelEdit() {
    this.expandedRows = {};
  }

  saveLoadCase(loadCase: LoadCaseDefinitionModel): void {
    this._loadCaseService.updateLoadCase(loadCase, null).subscribe(() => {
      this._messenger.publish(new DataSavedMessage('Post-Install Load Cases'));
      this.expandedRows = {};
      this.refreshLoadCases('edit', loadCase);
    });
  }

  selectLoad(): void {
    // Uses short timeout to prevent running pressTemp call twice on double-click.
    this.isSingleClick = true;
    setTimeout(() => {
      if (this.isSingleClick) {
        if (!this.drawerOpen) {
          return;
        }
        this.pressTempToggle();
      }
    }, 250);
  }

  async onRowReorder(e: any) {
    await lastValueFrom(this._loadCaseService.updateLoadCaseDefinitionOrder({ fromIndex: e.dragIndex, toIndex: e.dropIndex })).finally(async () => {
      this._messenger.publish(new DataSavedMessage('Post-Install Load Cases'));
      // Always reload
      this.loadCases = await lastValueFrom(this._loadCaseService.getLoadCasesSummary(true));
      if (!this.currentString.hasCement) {
        this.loadCases.forEach(x => {
          if (x.eppDisplayName == 'Fluid to Shoe') {
            x.eppDisplayName = 'Annular Fluid';
          }
        });
      }
    });
  }

  cloneLoadCase(load: LoadCaseSummaryResult) {
    const loadCase = { name: load.name + ' 1', index: load.index }
    this._loadCaseService.cloneLoadCaseDefinition(loadCase).subscribe({
      next: () => {
        this._messenger.publish(new DataSavedMessage('Post-Install Load Cases'));
        this.refreshLoadCases('clone', loadCase);
      },
      error: (e) => {
        console.log(e);
      }
    });
  }

  public selectLoadCases(e: any) {
    if (this.selectedLoadCaseIndexes.includes(e.index)) {
      const index = this.selectedLoadCaseIndexes.indexOf(e.index);
      this.selectedLoadCaseIndexes.splice(index, 1);
      return;
    } else {
      this.selectedLoadCaseIndexes.push(e.index);
    }
  }

  public toggleSelectAll(): void {
    this.selectAllChecked = !this.selectAllChecked
    if (this.selectAllChecked) {
      this.selectedLoadCaseIndexes = this.loadCases.filter(x => x.name != 'Initial Condition').map(x => x.index);
    } else {
      this.selectedLoadCaseIndexes = [];
    }
  }

  public deleteLoadCases() {
    this._confirmationService.confirm({
      message: 'Are you sure that you want to delete the selected load cases?',
      accept: () => {
        this.selectedLoadCaseIndexes = this.selectedLoadCaseIndexes.sort((a, b) => a - b);
        this._loadCaseService.deleteMultipleLoadCases(this.selectedLoadCaseIndexes).subscribe(() => {
          this.selectedLoadCaseIndexes = [];
          this.refreshLoadCases('deleteMultiple');
          this.selectAllChecked = false;
        });
      }
    });
  }

  deleteLoadCase(load: LoadCaseSummaryResult) {
    this._confirmationService.confirm({
      message: 'Are you sure that you want to delete load case ' + load.name.bold() + '?',
      accept: () => {
        this.isDelete = true;
        this.deletedLoadIndex = load.index;
        this._loadCaseService.deleteLoadCase(load.index).subscribe(() => {
          const index = this.loadCases.findIndex(x => x.index === load.index);
          this.loadCases.splice(index, 1);
          this.selectedLoad = this.loadCases[0];
          this.drawerOpen = false;
          this.postInstallLoadsStore.update(state => ({ ...state, drawerOpen: false, selectedLoad: this.selectedLoad }));
          if (this.selectedLoad?.index === load.index) {
            if (!this.selectedLoad) {
              this.plotData = null;
              return;
            }
          }
          this.refreshLoadCases('delete', load);
          this._messenger.publish(new DataSavedMessage('Post-Install Load Cases'));
          this.isDelete = false;
        });
      }
    });
  }

  ngOnDestroy(): void {
    this.signalRfunc = null;
  }
}